import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssessRequestRaService } from 'src/app/assess-request-ra/assess-request-ra.service';
import { ExportExcelButtonComponent } from 'src/app/common/components/export-excel-button/export-excel-button.component';
import { BULK_UPLOAD_COLUMNS, BULK_INVALID_EXPORT_COLUMNS } from 'src/app/common/models/tableColumns';
import { environment } from 'src/environments/environment';

interface EditBulkUploadSummary {
  inputCount: number;
  validCount: number;
  invalidCount: number;
}

interface EditBulkSubstanceTemplateData {
  [key: string]: any;
}

interface EditBulkUploadRow {
  rowNo: number;
  pureCode: string;
  sirId: string;
  inciName: string;
  cas: string;
  parentCAS: string;
  ins: string;
  synonyms: string;
  cfInput: string;
  cfData: string;
  errorMessage: string;
  selected: boolean;
  apiIsValid: boolean;
  invalidTemplateRow?: any;
}

interface EditAddSubstanceRow {
  [key: string]: any;
  pureCode: string;
  sirId: string;
  inciName: string;
  cas: string;
  parentCAS: string;
  ins: string;
  einecs: string;
  synonyms: string;
  cfInput: string;
  cfData: string;
  notifySers: boolean;
  notifySir: boolean;
  selected?: boolean;
}

@Component({
  selector: 'app-edit-group',
  templateUrl: './edit-group.component.html',
  styleUrls: ['./edit-group.component.scss']
})
export class EditGroupComponent implements OnInit {
  @ViewChild('bulkInvalidExportRef') bulkInvalidExportRef!: ExportExcelButtonComponent;
  groupId = '';
  groupName = '';
  groupNameNew = '';
  groupType = '';
  regulatoryGroup = '';
  division = '';
  substanceTableData: any[] = [];
  substanceTableColumns: any[] = [];
  loading = false;
  editingGroupName = false;
  isBulkUploadDialogVisible = false;
  bulkUploadFile: File | null = null;
  selectedBulkFileName = '';
  bulkUploadFileName = '';
  isBulkFileNameValid = false;
  readonly bulkFileNamePattern = /^[A-Za-z0-9 ]+$/;
  maxBulkUploadRows = 1000;
  bulkUploadStatusDialog = false;
  bulkUploadResultDialog = false;
  isBulkSelectAllChecked = false;
  showOnlyInvalidBulkRows = false;
  bulkInvalidExportDownloaded = false;
  bulkUploadSummary: EditBulkUploadSummary = {
    inputCount: 0,
    validCount: 0,
    invalidCount: 0
  };
  bulkUploadRows: EditBulkUploadRow[] = [];
  bulkRowsForView: EditBulkUploadRow[] = [];
  bulkUploadPageSize = 25;
  bulkUploadColumns = BULK_UPLOAD_COLUMNS;
  bulkInvalidExportColumns = BULK_INVALID_EXPORT_COLUMNS;
  isAddSubstanceDialogVisible = false;
  addSearchIdentifier = '';
  addSearchValue = '';
  addSearchResults: EditAddSubstanceRow[] = [];
  addSearchSuggestions: any[] = [];
  addSelectedSuggestionRows: EditAddSubstanceRow[] = [];
  showAddSuggestions = false;
  substanceIdentifierOptions = ['Pure Code', 'INCI Name', 'CAS #', 'SIR ID', 'Substance Name'];
  localSubstanceCatalog: EditAddSubstanceRow[] = [];
  addSubstanceColumns: any[] = [
    { field: 'check', header: '', width: '35px' },
    { field: 'pureCode', header: 'PURE Code', width: '120px' },
    { field: 'sirId', header: 'SIR ID', width: '120px' },
    { field: 'inciName', header: 'INCI Name', width: '150px' },
    { field: 'cas', header: 'CAS#', width: '110px' },
    { field: 'parentCAS', header: 'Parent CAS#', width: '120px' },
    { field: 'ins', header: 'INS#', width: '95px' },
    { field: 'einecs', header: 'EINECS', width: '110px' },
    { field: 'synonyms', header: 'Substance name', width: '180px' },
    { field: 'cfData', header: 'CF Data', width: '220px' },
    { field: 'notifySers', header: 'Notify SERS', width: '95px' },
    { field: 'notifySir', header: 'Notify SIR', width: '95px' }
  ];

  constructor(
    private assessRequestRa: AssessRequestRaService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private matSnackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.groupId = params['id'];
      if (this.groupId) {
        this.loadGroupDetails();
      }
    });
    this.route.queryParams.subscribe(params => {
      this.groupName = params['groupName'] || '';
      this.groupNameNew = this.groupName;
      this.groupType = params['groupType'] || 'Regulatory Group';
      this.division = params['division'] || '';
      this.regulatoryGroup = 'Regulatory Group';
    });
    this.initializeTableColumns();

    const navigation = this.router.getCurrentNavigation();
    const stateData = navigation && navigation.extras && navigation.extras.state
      ? navigation.extras.state['substanceTableData']
      : null;
    const historyStateData = window.history && window.history.state
      ? window.history.state.substanceTableData
      : null;

    if (Array.isArray(stateData) && stateData.length > 0) {
      this.substanceTableData = stateData;
    } else if (Array.isArray(historyStateData) && historyStateData.length > 0) {
      this.substanceTableData = historyStateData;
    }
  }

  loadGroupDetails(): void {
    this.loading = true;
    // Backend API call will be added here
    // this.administrationService.getGroupDetails(this.groupId).subscribe(...)
    this.loading = false;
  }

  initializeTableColumns(): void {
    this.substanceTableColumns = [
      { field: 'pureCode', header: 'PURE Code', width: '120px' },
      { field: 'sirId', header: 'SIR ID', width: '120px' },
      { field: 'inciName', header: 'INCI Name', width: '150px' },
      { field: 'cas', header: 'CAS#', width: '100px' },
      { field: 'parentCAS', header: 'Parent CAS#', width: '110px' },
      { field: 'ins', header: 'INS#', width: '90px' },
      { field: 'einecs', header: 'EINECS', width: '95px' },
      { field: 'synonyms', header: 'Synonyms', width: '150px' },
      { field: 'cfInput', header: 'CF Input', width: '120px' },
      { field: 'cfData', header: 'CF Data', width: '120px' },
      { field: 'notifySers', header: 'Notify SERS', width: '95px' },
      { field: 'notifySir', header: 'Notify SIR', width: '95px' }
    ];
  }

  onAddSubstance(): void {
    this.resetAddSubstanceState();
    this.isAddSubstanceDialogVisible = true;
  }

  onAddSubstanceSearch(): void {
    if (!this.addSearchIdentifier || !this.addSearchValue || !this.addSearchValue.trim()) {
      this.matSnackBar.open('Select search identifier and enter value', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    const identifier = this.getAddSearchApiIdentifier(this.addSearchIdentifier);
    const apiurl = environment.APIFAsTRule + '/rules/substances/' + identifier + '/cfRequired/y';
    const payload = [this.addSearchValue.trim()];

    this.loading = true;
    this.assessRequestRa.postData(apiurl, payload).subscribe((response: any[]) => {
      const rows = this.getAddSearchRows(response);
      this.addSearchSuggestions = rows;
      this.showAddSuggestions = rows.length > 0;
      this.loading = false;

      if (this.addSearchResults.length === 0) {
        this.matSnackBar.open('No substances found for the given search', 'close', {
          duration: 3000,
          panelClass: ['error']
        });
      }
    }, () => {
      this.loading = false;
      this.addSearchSuggestions = [];
      this.showAddSuggestions = false;
      this.addSearchResults = [];
      this.matSnackBar.open('Unable to fetch substances. Please verify search value.', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
    });
  }

  private getAddSearchRows(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.substances)) {
      return response.substances;
    }
    if (Array.isArray(response.substanceData)) {
      return response.substanceData;
    }
    if (Array.isArray(response.searchResults)) {
      return response.searchResults;
    }
    if (Array.isArray(response.items)) {
      return response.items;
    }
    if (Array.isArray(response.rows)) {
      return response.rows;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.result)) {
      return response.result;
    }

    if (response.data && typeof response.data === 'object') {
      const dataNode = response.data;
      if (Array.isArray(dataNode.substances)) {
        return dataNode.substances;
      }
      if (Array.isArray(dataNode.rows)) {
        return dataNode.rows;
      }
      if (Array.isArray(dataNode.result)) {
        return dataNode.result;
      }
    }

    if (response.result && typeof response.result === 'object') {
      const resultNode = response.result;
      if (Array.isArray(resultNode.substances)) {
        return resultNode.substances;
      }
      if (Array.isArray(resultNode.rows)) {
        return resultNode.rows;
      }
    }

    // Some responses return a single substance object.
    if (response.sirId || response.SIRID || response.sirID || response.pureCode || response.purecode || response.substanceName) {
      return [response];
    }

    return [];
  }

  addSelectedSuggestionsToPopupTable(): void {
    const selectedRows = [...this.addSelectedSuggestionRows];

    if (selectedRows.length === 0) {
      this.matSnackBar.open('Select at least one substance to add', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    const existingPopupKeys = new Set((this.addSearchResults || []).map((item: any) => item.sirId || item.pureCode));
    const rowsToAddInPopup = selectedRows.filter((row) => !existingPopupKeys.has(row.sirId || row.pureCode));

    if (rowsToAddInPopup.length === 0) {
      this.matSnackBar.open('Selected substances are already added in popup table', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    this.addSearchResults = [...this.addSearchResults, ...rowsToAddInPopup.map((row) => ({ ...row, selected: false }))];
    this.addSelectedSuggestionRows = [];
    this.addSearchSuggestions = [];
    this.showAddSuggestions = false;
    this.matSnackBar.open(rowsToAddInPopup.length + ' substance(s) added to popup table', 'close', {
      duration: 3000,
      panelClass: ['success']
    });
  }

  saveSelectedAddSubstances(): void {
    let selectedRows = this.addSearchResults.filter(row => row.selected);

    if (selectedRows.length === 0) {
      selectedRows = [...this.addSearchResults];
    }

    if (selectedRows.length === 0) {
      this.matSnackBar.open('No substances available in popup table to save', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    const existingSirIds = new Set((this.substanceTableData || []).map((item: any) => item.sirId));
    const rowsToAdd = selectedRows.filter(row => !existingSirIds.has(row.sirId));

    if (rowsToAdd.length === 0) {
      this.matSnackBar.open('Selected substances are already part of the group', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    this.substanceTableData = [...this.substanceTableData, ...rowsToAdd.map(row => ({
      pureCode: row.pureCode,
      sirId: row.sirId,
      inciName: row.inciName,
      cas: row.cas,
      parentCAS: row.parentCAS,
      ins: row.ins,
      einecs: row.einecs,
      synonyms: row.synonyms,
      cfInput: row.cfInput || '',
      cfData: row.cfData,
      notifySers: false,
      notifySir: false
    }))];

    this.isAddSubstanceDialogVisible = false;
    this.addSearchSuggestions = [];
    this.addSelectedSuggestionRows = [];
    this.showAddSuggestions = false;
    this.matSnackBar.open(rowsToAdd.length + ' substance(s) added successfully', 'close', {
      duration: 3000,
      panelClass: ['success']
    });
  }

  onAddSearchSuggestionToggle(item: any, event: any): void {
    const mapped = this.mapAddSearchApiRow(item);
    const identityKey = mapped.sirId || mapped.pureCode;
    const checked = !!(event && event.target && event.target.checked);

    if (checked) {
      const alreadySelected = this.addSelectedSuggestionRows.some((row) => (row.sirId || row.pureCode) === identityKey);
      if (!alreadySelected) {
        this.addSelectedSuggestionRows.push(mapped);
      }
    } else {
      this.addSelectedSuggestionRows = this.addSelectedSuggestionRows.filter((row) => (row.sirId || row.pureCode) !== identityKey);
    }
  }

  onAddSearchValueChange(): void {
    if (!this.addSearchValue || !this.addSearchValue.trim()) {
      this.addSearchSuggestions = [];
      this.addSelectedSuggestionRows = [];
      this.showAddSuggestions = false;
    }
  }

  onBulkUploadSubstance(): void {
    this.resetBulkUploadState();
    this.isBulkUploadDialogVisible = true;
  }

  onBulkFileSelect(event: any): void {
    const selectedFile = event && event.files && event.files.length ? event.files[0] : null;
    this.bulkUploadFile = selectedFile || null;
    this.selectedBulkFileName = selectedFile ? selectedFile.name : '';
    this.bulkUploadFileName = this.selectedBulkFileName;
    this.isBulkFileNameValid = this.validateBulkFileName(this.bulkUploadFileName, false) &&
      this.validateBulkFileType(this.bulkUploadFileName, false);
  }

  onBulkFileClear(): void {
    this.bulkUploadFile = null;
    this.selectedBulkFileName = '';
    this.bulkUploadFileName = '';
    this.isBulkFileNameValid = false;
  }

  onBulkUploadConfirm(event?: any, fileuploadForm?: any): void {
    const selectedFile = event && event.files && event.files.length ? event.files[0] : this.bulkUploadFile;
    if (!selectedFile) {
      this.matSnackBar.open('Please select a file', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    this.bulkUploadFile = selectedFile;
    this.bulkUploadFileName = selectedFile.name;
    this.selectedBulkFileName = selectedFile.name;

    if (!this.validateBulkFileName(selectedFile.name, true) || !this.validateBulkFileType(selectedFile.name, true)) {
      return;
    }

    this.loading = true;
    const apiurl = environment.APIFAsTRule + '/bulksubstance/validatetemplate';
    const formData = new FormData();
    formData.append('file', selectedFile, selectedFile.name);

    const userDetails = JSON.parse(localStorage.getItem('userRolesPermissions') || '{}');
    const bearerToken = localStorage.getItem('FunctionToken') || localStorage.getItem('Token') || '';
    let headerdata = new HttpHeaders({
      Authorization: 'Bearer ' + bearerToken,
      'X-FAST-AUTH-K': '6ee2a098-e2d6-4893-b8b4-18f01c9a67a1-6DB1487E-C741-4669-B8BB-9F34EE294EEF',
      'X-Requested-With': 'XMLHttpRequest'
    });
    if (userDetails && userDetails.userId !== undefined && userDetails.userId !== null) {
      headerdata = headerdata.set('userId', String(userDetails.userId));
    }

    this.http.post<any>(apiurl, formData, { headers: headerdata }).subscribe((response) => {
      const mappedResponse = this.mapBulkValidationApiResponse(response);
      this.bulkUploadSummary = {
        inputCount: mappedResponse.inputCount,
        validCount: mappedResponse.validCount,
        invalidCount: mappedResponse.invalidCount
      };
      this.bulkUploadRows = mappedResponse.rows;
      this.showOnlyInvalidBulkRows = false;
      this.refreshBulkRowsForView();
      this.isBulkSelectAllChecked = false;
      this.bulkUploadStatusDialog = true;
      this.finishBulkUploadRequest(fileuploadForm);
    }, (error) => {
      const status = error && error.status ? ' (' + error.status + ')' : '';
      const message = error && error.status === 401
        ? 'You do not have permission to validate this template. Please contact FAsT support.'
        : 'Unable to validate upload file. Please use valid template format.';
      this.matSnackBar.open(message + status, 'close', {
        duration: 4500,
        panelClass: ['error']
      });
      this.finishBulkUploadRequest(fileuploadForm);
    });
  }

  private finishBulkUploadRequest(fileuploadForm?: any): void {
    this.loading = false;
    if (fileuploadForm) {
      fileuploadForm.clear();
    }
    this.onBulkFileClear();
    this.isBulkUploadDialogVisible = false;
  }

  private mapBulkValidationApiResponse(response: any): { inputCount: number; validCount: number; invalidCount: number; rows: EditBulkUploadRow[] } {
    const rows = this.getBulkValidationResponseRows(response);
    const mappedRows = rows.map((row, index) => this.mapBulkValidationApiRow(row, index + 1));
    const validCount = mappedRows.filter((row) => row.apiIsValid).length;
    const invalidCount = mappedRows.filter((row) => !row.apiIsValid).length;
    const inputCount = mappedRows.length;

    return {
      inputCount,
      validCount,
      invalidCount,
      rows: mappedRows
    };
  }

  private getBulkValidationResponseRows(response: any): EditBulkSubstanceTemplateData[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }

    const baseRows: any[] = [];
    if (Array.isArray(response.rows)) {
      baseRows.push(...response.rows);
    } else if (Array.isArray(response.data)) {
      baseRows.push(...response.data);
    } else if (Array.isArray(response.result)) {
      baseRows.push(...response.result);
    }

    // Some API shapes return invalid rows in dedicated arrays; include them so export button can be shown.
    if (Array.isArray(response.invalidRows)) {
      baseRows.push(...response.invalidRows);
    }
    if (Array.isArray(response.invalidData)) {
      baseRows.push(...response.invalidData);
    }
    if (Array.isArray(response.invalidSubstances)) {
      baseRows.push(...response.invalidSubstances);
    }

    if (baseRows.length === 0) {
      if (Array.isArray(response.validRows)) {
        baseRows.push(...response.validRows);
      }
      if (Array.isArray(response.validData)) {
        baseRows.push(...response.validData);
      }
      if (Array.isArray(response.validSubstances)) {
        baseRows.push(...response.validSubstances);
      }
    }

    return baseRows;
  }

  private mapBulkValidationApiRow(row: EditBulkSubstanceTemplateData, rowNo: number): EditBulkUploadRow {
    const rowData: any = row || {};
    const substance = rowData && (rowData.Substance || rowData.substance) ? (rowData.Substance || rowData.substance) : null;
    const isValid = this.getBulkApiIsValid(rowData);
    const pureCode = this.getBulkApiFieldValue(substance, ['PURE Code ', 'PURE Code', 'Pure Code', 'pureCode']) || '';
    const sirId = this.getBulkApiFieldValue(substance, ['SIR ID', 'SirId', 'sirId']) || '';
    const inciName = this.getBulkApiFieldValue(substance, ['INCI Name', 'InciName', 'inciName']) || '';
    const cas = this.getBulkApiFieldValue(substance, ['CAS #', 'CAS#', 'cas']) || '';
    const parentCAS = this.getBulkApiFieldValue(row, ['ParentCASnumber', 'parentCASnumber', 'Parent CAS Number']) || '';
    const ins = this.getBulkApiFieldValue(row, ['INSNumber', 'insNumber', 'INS Number']) || '';
    const synonyms = this.getBulkApiFieldValue(row, ['Synonyms', 'synonyms'])
      || this.getBulkApiFieldValue(substance, ['Synonyms', 'synonyms', 'Substance name', 'Substance Name'])
      || '';
    const cfInput = this.getBulkApiFieldValue(row, ['CFInputData', 'cfInputData', 'CF Input Data']) || '';
    const cfData = this.getBulkApiFieldValue(row, ['CFDataFromSIR', 'cfDataFromSIR', 'CF Data From SIR']) || '';
    const errorMessage = isValid
      ? ''
      : (this.getBulkApiFieldValue(row, ['Comments', 'comments', 'ErrorMessage', 'errorMessage', 'ValidationMessage', 'validationMessage', 'Reason', 'reason']) || 'Invalid data');

    return {
      rowNo,
      pureCode,
      sirId,
      inciName,
      cas,
      parentCAS,
      ins,
      synonyms,
      cfInput,
      cfData,
      errorMessage,
      selected: false,
      apiIsValid: isValid,
      invalidTemplateRow: {
        'PURE Code': pureCode,
        'SIR ID': sirId,
        'CAS #': cas,
        EINECS: this.getBulkApiFieldValue(substance, ['EINECS', 'Einecs', 'einecs']) || '',
        CF: this.getBulkApiFieldValue(substance, ['CF', 'cf']) || cfData,
        'INCI Name': inciName,
        'Substance name': this.getBulkApiFieldValue(substance, ['Substance name', 'Substance Name', 'substanceName']) || synonyms
      }
    };
  }

  private getBulkApiFieldValue(source: any, keys: string[]): string {
    if (!source) {
      return '';
    }
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
        return String(source[key]).trim();
      }
    }
    return '';
  }

  private getBulkApiIsValid(rowData: any): boolean {
    if (rowData && typeof rowData.IsValid === 'boolean') {
      return rowData.IsValid;
    }
    if (rowData && typeof rowData.isValid === 'boolean') {
      return rowData.isValid;
    }

    const substance = rowData && (rowData.Substance || rowData.substance) ? (rowData.Substance || rowData.substance) : null;
    if (substance && typeof substance.IsValid === 'boolean') {
      return substance.IsValid;
    }
    if (substance && typeof substance.isValid === 'boolean') {
      return substance.isValid;
    }

    const normalizedStatus = String(
      (rowData && (rowData.rowStatus || rowData.RowStatus || rowData.status || rowData.Status || rowData.validationStatus || rowData.ValidationStatus)) || ''
    ).trim().toLowerCase();

    if (normalizedStatus === 'valid' || normalizedStatus === 'true' || normalizedStatus === 'yes' || normalizedStatus === 'y') {
      return true;
    }
    if (normalizedStatus === 'invalid' || normalizedStatus === 'false' || normalizedStatus === 'no' || normalizedStatus === 'n') {
      return false;
    }

    const hasErrorText = !!String(
      (rowData && (rowData.Comments || rowData.comments || rowData.ErrorMessage || rowData.errorMessage || rowData.ValidationMessage || rowData.validationMessage || rowData.Reason || rowData.reason)) || ''
    ).trim();
    if (hasErrorText) {
      return false;
    }

    return false;
  }

  onBulkStatusContinue(): void {
    this.bulkUploadStatusDialog = false;
    this.showOnlyInvalidBulkRows = false;
    this.refreshBulkRowsForView();
    this.bulkUploadResultDialog = true;
  }

  getBulkRowsForView(): EditBulkUploadRow[] {
    return this.bulkRowsForView;
  }

  getBulkInvalidRows(): EditBulkUploadRow[] {
    return this.bulkUploadRows.filter(row => !row.apiIsValid);
  }

  hasBulkInvalidRows(): boolean {
    return this.getBulkInvalidRows().length > 0 || this.bulkUploadSummary.invalidCount > 0;
  }

  getBulkInvalidRowsForExport(): any[] {
    return this.getBulkInvalidRows().map((row) => {
      if (row.invalidTemplateRow) {
        return row.invalidTemplateRow;
      }
      return {
        'PURE Code': row.pureCode || '',
        'SIR ID': row.sirId || '',
        'CAS #': row.cas || '',
        EINECS: '',
        CF: row.cfData || '',
        'INCI Name': row.inciName || '',
        'Substance name': row.synonyms || ''
      };
    });
  }

  toggleBulkInvalidRowsView(): void {
    this.showOnlyInvalidBulkRows = false;
    this.isBulkSelectAllChecked = false;
    this.refreshBulkRowsForView();

    if (this.bulkInvalidExportRef && this.getBulkInvalidRows().length > 0) {
      this.bulkInvalidExportDownloaded = true;
      setTimeout(() => {
        this.bulkInvalidExportRef.exportToExcel();
      }, 0);
    }
  }

  onBulkRowSelectAll(event: any): void {
    const isChecked = event && event.target ? event.target.checked : false;
    this.isBulkSelectAllChecked = isChecked;
    this.getBulkRowsForView().forEach(row => {
      if (row.apiIsValid) {
        row.selected = isChecked;
      }
    });
  }

  onBulkRowSelectionChange(): void {
    const validRows = this.getBulkRowsForView().filter(row => row.apiIsValid);
    this.isBulkSelectAllChecked = validRows.length > 0 && validRows.every(row => row.selected);
  }

  getSelectedBulkValidRowsCount(): number {
    return this.bulkUploadRows.filter(row => row.selected && row.apiIsValid).length;
  }

  canSaveBulkValidSubstances(): boolean {
    const hasSelectedValidRows = this.getSelectedBulkValidRowsCount() > 0;
    const requiresInvalidExport = this.bulkUploadSummary.invalidCount > 0;
    return hasSelectedValidRows && (!requiresInvalidExport || this.bulkInvalidExportDownloaded);
  }

  saveBulkValidSubstances(): void {
    const validRowsToSave = this.bulkUploadRows.filter(row => row.selected && row.apiIsValid);
    if (validRowsToSave.length === 0) {
      this.matSnackBar.open('Select at least one valid substance to save', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
      return;
    }

    const selectedSirIds = Array.from(new Set(validRowsToSave
      .map((row) => row.sirId)
      .filter((sirId: string) => !!sirId && !sirId.includes('SIR ID Not found'))));

    if (selectedSirIds.length === 0) {
      this.matSnackBar.open('No valid SIR IDs found to save', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
      return;
    }

    const existingSirIds = new Set((this.substanceTableData || []).map((item: any) => item.sirId));
    const mergedSirIds = new Set(Array.from(existingSirIds));
    selectedSirIds.forEach((sirId) => mergedSirIds.add(sirId));

    if (mergedSirIds.size === existingSirIds.size) {
      this.matSnackBar.open('Selected substances are already part of the group', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
      return;
    }

    if (!this.groupId) {
      this.matSnackBar.open('Group ID is missing. Reload the page and try again.', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
      return;
    }

    const apiurl = environment.APIFAsTRule + '/rules/editsubstancegroup';
    const payload: any = {
      groupId: this.groupId,
      groupName: this.groupName,
      groupType: this.groupType || 'Regulatory Group',
      UPDFullText: this.division,
      SubstanceIds: Array.from(mergedSirIds)
    };

    const userDetails = JSON.parse(localStorage.getItem('userRolesPermissions') || '{}');
    const bearerToken = localStorage.getItem('FunctionToken') || localStorage.getItem('Token') || '';
    let headerdata = new HttpHeaders({
      Authorization: 'Bearer ' + bearerToken,
      'X-FAST-AUTH-K': '6ee2a098-e2d6-4893-b8b4-18f01c9a67a1-6DB1487E-C741-4669-B8BB-9F34EE294EEF',
      'X-Requested-With': 'XMLHttpRequest'
    });
    if (userDetails && userDetails.userId !== undefined && userDetails.userId !== null) {
      headerdata = headerdata.set('userId', String(userDetails.userId));
    }

    this.loading = true;
    const request$ = this.http.put<any>(apiurl, payload, { headers: headerdata });

    request$.subscribe((response) => {
      const recordsToAdd = validRowsToSave
        .filter((row) => !existingSirIds.has(row.sirId))
        .map((row) => ({
          pureCode: row.pureCode,
          sirId: row.sirId,
          inciName: row.inciName,
          cas: row.cas,
          parentCAS: row.parentCAS,
          ins: row.ins,
          einecs: '',
          synonyms: row.synonyms,
          cfData: row.cfData,
          notifySers: false,
          notifySir: false,
          cfInput: row.cfInput
        }));

      this.substanceTableData = [...this.substanceTableData, ...recordsToAdd];
      this.groupId = String((response && (response.groupId || response.GroupId || response.groupID || response.GroupID)) || this.groupId || '');
      this.groupName = (response && (response.groupName || response.GroupName || response.groupNAME)) || this.groupName;
      this.bulkUploadResultDialog = false;
      this.loading = false;
      this.matSnackBar.open(recordsToAdd.length + ' substance(s) added from bulk upload', 'close', {
        duration: 3500,
        panelClass: ['success']
      });
    }, () => {
      this.loading = false;
      this.matSnackBar.open('Failed to save bulk upload substances', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
    });
  }

  private validateBulkFileName(fileName: string, showError: boolean): boolean {
    const nameWithoutExtension = (fileName || '').replace(/\.[^/.]+$/, '');
    const isValid = this.bulkFileNamePattern.test(nameWithoutExtension);
    if (!isValid && showError) {
      this.matSnackBar.open('Bulk upload file name must be alphanumeric and English only', 'close', {
        duration: 4000,
        panelClass: ['error']
      });
    }
    return isValid;
  }

  private validateBulkFileType(fileName: string, showError: boolean): boolean {
    const lowerName = (fileName || '').toLowerCase();
    const isValid = lowerName.endsWith('.xlsx');
    if (!isValid && showError) {
      this.matSnackBar.open('Please upload only .xlsx file format', 'close', {
        duration: 4000,
        panelClass: ['error']
      });
    }
    return isValid;
  }

  resetBulkUploadState(): void {
    this.bulkUploadFile = null;
    this.selectedBulkFileName = '';
    this.bulkUploadFileName = '';
    this.isBulkFileNameValid = false;
    this.bulkUploadStatusDialog = false;
    this.bulkUploadResultDialog = false;
    this.isBulkSelectAllChecked = false;
    this.showOnlyInvalidBulkRows = false;
    this.bulkInvalidExportDownloaded = false;
    this.bulkUploadSummary = {
      inputCount: 0,
      validCount: 0,
      invalidCount: 0
    };
    this.bulkUploadRows = [];
    this.bulkRowsForView = [];
  }

  private refreshBulkRowsForView(): void {
    this.bulkRowsForView = this.bulkUploadRows.filter(row => row.apiIsValid);
  }

  private initializeLocalSubstanceCatalog(): void {
    const fromExisting = (this.substanceTableData || []).map((row: any) => {
      return {
        pureCode: row.pureCode || row.purecode || '',
        sirId: row.sirId || '',
        inciName: row.inciName || '',
        cas: row.cas || '',
        parentCAS: row.parentCAS || '',
        ins: row.ins || '',
        einecs: row.einecs || '',
        synonyms: row.synonyms || '',
        cfInput: row.cfInput || row.cfinput || row.CFInputData || row.cfInputData || '',
        cfData: row.cfData || row.cfdata || '',
        notifySers: false,
        notifySir: false,
        selected: false
      } as EditAddSubstanceRow;
    });

    const fallbackData: EditAddSubstanceRow[] = [
      {
        pureCode: 'PURE700002',
        sirId: 'SIR-000-045-356',
        inciName: '',
        cas: '64-19-7',
        parentCAS: '',
        ins: '',
        einecs: '',
        synonyms: '',
        cfInput: '',
        cfData: '1',
        notifySers: false,
        notifySir: false,
        selected: false
      },
      {
        pureCode: 'PURE722538',
        sirId: 'SIR-000-045-353',
        inciName: '',
        cas: '',
        parentCAS: '',
        ins: '',
        einecs: '',
        synonyms: '',
        cfInput: '',
        cfData: '1',
        notifySers: false,
        notifySir: false,
        selected: false
      }
    ];

    const merged = [...fromExisting, ...fallbackData];
    const uniqueBySirId: { [sirId: string]: EditAddSubstanceRow } = {};
    merged.forEach((item) => {
      if (item && item.sirId) {
        uniqueBySirId[item.sirId] = item;
      }
    });
    this.localSubstanceCatalog = Object.keys(uniqueBySirId).map((key) => uniqueBySirId[key]);
  }

  private resetAddSubstanceState(): void {
    this.addSearchIdentifier = '';
    this.addSearchValue = '';
    this.addSearchResults = [];
    this.addSearchSuggestions = [];
    this.addSelectedSuggestionRows = [];
    this.showAddSuggestions = false;
    this.localSubstanceCatalog = [];
  }

  private getAddSearchApiIdentifier(identifier: string): string {
    if (identifier === 'CAS #') {
      return 'CAS';
    }
    if (identifier === 'Substance Name') {
      return 'BACKBONEIDENTITY';
    }
    if (identifier === 'INCI Name') {
      return 'INCI';
    }
    if (identifier === 'SIR ID') {
      return 'SIRID';
    }
    return 'PURECODE';
  }

  private mapAddSearchApiRow(item: any): EditAddSubstanceRow {
    const cfDetails = Array.isArray(item && item.cfDetails) ? item.cfDetails : [];
    const cfDisplayValues = cfDetails
      .map((cf: any) => {
        if (!cf) {
          return '';
        }
        return cf.cfDisplayText || cf.cfdisplaytext || cf.displayText || '';
      })
      .filter((value: string) => !!value);

    const pureCode = this.getAddApiFieldValue(item, ['pureCode', 'purecode', 'PURE Code', 'PURE Code ']);
    const sirId = this.getAddApiFieldValue(item, ['sirId', 'SIRID', 'SirId', 'SIR ID']);
    const inciName = this.getAddApiFieldValue(item, ['inciName', 'InciName', 'INCI Name']);
    const cas = this.getAddApiFieldValue(item, ['cas', 'CAS #', 'CAS#']);
    const parentCAS = this.getAddApiFieldValue(item, ['parentCAS', 'ParentCASnumber', 'Parent CAS Number']);
    const ins = this.getAddApiFieldValue(item, ['ins', 'INSNumber', 'INS Number']);
    const einecs = this.getAddApiFieldValue(item, ['einecs', 'EINECS', 'Einecs']);
    const synonyms = this.getAddApiFieldValue(item, ['substanceName', 'Substance name', 'Substance Name', 'synonyms', 'Synonyms']);
    const cfInput = this.getAddApiFieldValue(item, ['cfInputData', 'cfInput', 'CFInputData', 'CF Input Data']);
    const cfData = cfDisplayValues.toString() || this.getAddApiFieldValue(item, ['cfData', 'CFDataFromSIR', 'CF Data From SIR']);

    return {
      pureCode,
      sirId,
      inciName,
      cas,
      parentCAS,
      ins,
      einecs,
      synonyms,
      cfInput,
      cfData,
      notifySers: false,
      notifySir: false,
      selected: false
    };
  }

  private getAddApiFieldValue(source: any, keys: string[]): string {
    if (!source) {
      return '';
    }
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  private getAddSearchFieldName(identifier: string): string {
    if (identifier === 'Pure Code') {
      return 'pureCode';
    }
    if (identifier === 'INCI Name') {
      return 'inciName';
    }
    if (identifier === 'CAS #') {
      return 'cas';
    }
    if (identifier === 'SIR ID') {
      return 'sirId';
    }
    return 'synonyms';
  }

  onEditGroupName(): void {
    if (!this.editingGroupName) {
      this.groupNameNew = this.groupName;
      this.editingGroupName = true;
      return;
    }

    const updatedGroupName = (this.groupNameNew || '').trim();
    if (!updatedGroupName) {
      this.matSnackBar.open('Group name is required', 'close', {
        duration: 3000,
        panelClass: ['error']
      });
      return;
    }

    if (!this.groupId) {
      this.matSnackBar.open('Group ID is missing. Reload the page and try again.', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
      return;
    }

    if (updatedGroupName === (this.groupName || '').trim()) {
      this.editingGroupName = false;
      return;
    }

    const apiurl = environment.APIFAsTRule + '/rules/updategroup';
    const payload: any = {
      groupId: this.groupId,
      groupName: updatedGroupName,
      groupType: this.groupType || 'Regulatory Group',
      UPDFullText: this.division,
      substanceData: this.substanceTableData || []
    };

    const userDetails = JSON.parse(localStorage.getItem('userRolesPermissions') || '{}');
    const bearerToken = localStorage.getItem('FunctionToken') || localStorage.getItem('Token') || '';
    let headerdata = new HttpHeaders({
      Authorization: 'Bearer ' + bearerToken,
      'X-FAST-AUTH-K': '6ee2a098-e2d6-4893-b8b4-18f01c9a67a1-6DB1487E-C741-4669-B8BB-9F34EE294EEF',
      'X-Requested-With': 'XMLHttpRequest'
    });
    if (userDetails && userDetails.userId !== undefined && userDetails.userId !== null) {
      headerdata = headerdata.set('userId', String(userDetails.userId));
    }

    this.loading = true;
    this.http.put<any>(apiurl, payload, { headers: headerdata }).subscribe((response) => {
      this.groupName = (response && (response.groupName || response.GroupName || response.groupNAME)) || updatedGroupName;
      this.groupNameNew = this.groupName;
      this.editingGroupName = false;
      this.loading = false;
      this.matSnackBar.open('Group name updated', 'close', {
        duration: 3000,
        panelClass: ['success']
      });
    }, () => {
      this.loading = false;
      this.matSnackBar.open('Failed to update group name', 'close', {
        duration: 3500,
        panelClass: ['error']
      });
    });
  }

  onBack(): void {
    this.router.navigate(['/administration/regulatory-group-master']);
  }

  onDeleteSubstance(substance: any): void {
    const index = this.substanceTableData.indexOf(substance);
    if (index > -1) {
      this.substanceTableData.splice(index, 1);
      this.matSnackBar.open('Substance removed', 'close', {
        duration: 3000,
        panelClass: ['success']
      });
    }
  }
}
